import { profileFetch } from "../profileSession";
import { API_BASE, getToken, ApiError, req, privateDocumentBlob } from "./transport";
import type { CompanyLicenseStatus, CompanyRegistrationDetails, CompanyLicense, AdminCompanyLicense, LegacyCompanyVerification, AuthUser, TaxonomyPayload, OrgProfilePayload } from "./types";

export const companiesApi = {
  // v3.9.65 (Stage 6 / I) — company storefront (their items + services)
  async companyStorefront(id: string) {
    return req<{ items: any[]; services: any[] }>(`/companies/${encodeURIComponent(id)}/storefront`);
  },

  // v3.9.67 (QA #3/#4) — company follower count + owner delete
  async companyFollowers(id: string) {
    return req<{ count: number }>(`/companies/${encodeURIComponent(id)}/followers`);
  },

  async deleteCompany(id: string) {
    return req<{ ok: boolean; deleted: string; user: AuthUser }>(`/companies/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Increment 17: Companies vertical
  async myCompanies(signal?: AbortSignal) {
    const r = await req<{ companies: any[] }>("/companies/mine", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.companies)) throw new Error("Missing owned Businesses.");
    return r?.companies ?? [];
  },

  // v3.9.0 — organization taxonomy + business profile
  async taxonomy() {
    // [business.taxonomy-and-readiness](../../../../docs/product/businesses.md#business-taxonomy-and-readiness): revalidate the catalog so cached descriptor choices cannot
    // outlive the server's current subtype validation after an update.
    return req<TaxonomyPayload>("/taxonomy", { cache: "no-cache" });
  },

  async orgTypes() {
    const r = await req<{ organizationTypes: { id: string; name: string }[] }>("/taxonomy/org-types");
    return r?.organizationTypes ?? [];
  },

  async similarCompanies(id: string) {
    const r = await req<{ items: any[] }>(`/marketplace/companies/${encodeURIComponent(id)}/similar`);
    return r?.items ?? [];
  },

  async suggestedOrgs(kind: "projects" | "rfqs", id: string) {
    const r = await req<{ items: any[] }>(`/marketplace/${kind}/${encodeURIComponent(id)}/suggested-orgs`);
    return r?.items ?? [];
  },

  async adminTaxonomyStats() {
    return req<{
      totals: { organizations: number; verified: number; profileComplete: number; withOrgType: number };
      catalog: { orgTypes: number; roleCategories: number; roles: number };
      byType: { id: string; name: string; count: number; verified: number }[];
      byCoverage: { label: string; count: number }[];
      topRoles: { label: string; count: number }[];
    }>("/admin/taxonomy-stats");
  },

  async createCompany(payload: OrgProfilePayload & { tags?: string[] }) {
    return req<{ item: any; kind: string }>("/listings/company", { method: "POST", body: JSON.stringify(payload) });
  },

  async getCompany(id: string) {
    const r = await req<{ item: any }>(`/companies/${encodeURIComponent(id)}/profile`);
    return r?.item ?? null;
  },

  async updateCompanyProfile(id: string, payload: OrgProfilePayload) {
    return req<{ item: any; complete: boolean }>(`/companies/${encodeURIComponent(id)}/profile`, { method: "PATCH", body: JSON.stringify(payload) });
  },

  companyLogoUrl(id: string) {
    return `${API_BASE}/companies/${encodeURIComponent(id)}/logo`;
  },

  companyCoverUrl(id: string) {
    return `${API_BASE}/companies/${encodeURIComponent(id)}/cover`;
  },

  companyMediaBlob(id: string, kind: "logo" | "cover", signal?: AbortSignal) {
    return privateDocumentBlob(`/companies/${encodeURIComponent(id)}/${kind}`, signal);
  },

  async uploadCompanyCover(id: string, file: File) {
    return req<{ ok: boolean }>(`/companies/${encodeURIComponent(id)}/cover`, {
      method: "POST", headers: { "Content-Type": file.type }, body: file,
    }, { rejectUnauthorized: true });
  },

  async removeCompanyCover(id: string) {
    return req<{ ok: boolean }>(`/companies/${encodeURIComponent(id)}/cover`, { method: "DELETE" }, { rejectUnauthorized: true });
  },

  async uploadCompanyLogo(id: string, file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/companies/${encodeURIComponent(id)}/logo`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
    return data;
  },

  // v3.9.0 Phase 4 — organization dashboard
  async companyDashboard(id: string, signal?: AbortSignal) {
    return req<{
      company: any; myRole: string;
      completeness: { percent: number; done: number; total: number; missing: string[] };
      licenses: { total: number; approved: number; pending: number; rejected: number };
      nextSteps: { label: string; href: string; done: boolean }[];
    }>(`/companies/${encodeURIComponent(id)}/dashboard`, { signal }, { rejectUnauthorized: signal !== undefined });
  },

  // v3.9.0 Phase 3 — licenses & verification (owner)
  async companyLicenses(id: string) {
    const r = await req<{ licenses: CompanyLicense[] }>(`/companies/${encodeURIComponent(id)}/licenses`, {}, { rejectUnauthorized: true });
    return r?.licenses ?? [];
  },

  async submitCompanyLicense(id: string, payload: CompanyRegistrationDetails, document: File, submissionId: string): Promise<{ license: CompanyLicense; verified: boolean }> {
    const form = new FormData();
    for (const [field, value] of Object.entries(payload)) if (value) form.append(field, value);
    form.append("submissionId", submissionId); form.append("document", document, document.name);
    const token = getToken();
    const response = await profileFetch(`${API_BASE}/companies/${encodeURIComponent(id)}/licenses`, { method: "POST", credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: form });
    const result = await response.json();
    if (!response.ok) throw new ApiError(response.status, result?.error || "Could not submit this registration.", result?.code);
    return result;
  },

  async uploadLicenseDoc(id: string, licId: string, file: File) {
    const token = getToken();
    const res = await profileFetch(`${API_BASE}/companies/${encodeURIComponent(id)}/licenses/${encodeURIComponent(licId)}/doc`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json();
    if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
    return data as { license: CompanyLicense; verified: boolean };
  },

  licenseDocUrl(id: string, licId: string) {
    return `${API_BASE}/companies/${encodeURIComponent(id)}/licenses/${encodeURIComponent(licId)}/doc`;
  },

  async deleteLicense(id: string, licId: string) {
    return req<{ ok: boolean; verified: boolean }>(`/companies/${encodeURIComponent(id)}/licenses/${encodeURIComponent(licId)}`, { method: "DELETE" }, { rejectUnauthorized: true });
  },

  async companyLicenseDocumentBlob(companyId: string, licenseId: string) {
    return privateDocumentBlob(`/companies/${encodeURIComponent(companyId)}/licenses/${encodeURIComponent(licenseId)}/doc`);
  },

  // admin review
  async adminCompanyLicenses(status: CompanyLicenseStatus = "pending", offset = 0) {
    return req<{ licenses: AdminCompanyLicense[]; total: number; offset: number; limit: number }>(`/admin/company-licenses?status=${status}&offset=${offset}`, {}, { rejectUnauthorized: true });
  },

  adminLicenseDocUrl(id: string) {
    return `${API_BASE}/admin/company-licenses/${encodeURIComponent(id)}/doc`;
  },

  async adminCompanyLicenseDocumentBlob(id: string, reviewVersion: string) {
    return privateDocumentBlob(`/admin/company-licenses/${encodeURIComponent(id)}/doc?reviewVersion=${encodeURIComponent(reviewVersion)}`);
  },

  async adminApproveLicense(id: string, reviewVersion: string) {
    return req<{ ok: boolean; verified: boolean }>(`/admin/company-licenses/${encodeURIComponent(id)}/approve`, { method: "POST", body: JSON.stringify({ reviewVersion }) }, { rejectUnauthorized: true });
  },

  async adminRejectLicense(id: string, note: string, reviewVersion: string) {
    return req<{ ok: boolean; verified: boolean }>(`/admin/company-licenses/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ note, reviewVersion }) }, { rejectUnauthorized: true });
  },

  async legacyCompanyVerifications(offset = 0) {
    return req<{ requests: LegacyCompanyVerification[]; total: number; offset: number; limit: number }>(`/verification/company-history?offset=${offset}`, {}, { rejectUnauthorized: true });
  }
};
